import React from "react";
import { ItemMetric, MetricType } from "components/common/ItemMetric";
import {
  HoverCardHeader,
  HoverCardList,
  HoverCardNote,
  HoverCardRow,
  HoverCardSkeleton,
} from "@/components/common/HoverCard";
import { ColumnPreview, TablePreviewState } from "./tablePreview";

interface Props {
  preview: TablePreviewState;
}

// `precise` drops the "~" prefix used for planner estimates. Returns null when
// rows ≤ 0 (never analyzed) so the caller can hide the metric entirely.
const formatRows = (rows: number, precise: boolean): string | null => {
  if (precise) return rows.toLocaleString();
  if (rows <= 0) return null;
  if (rows < 1000) return `~${rows}`;
  if (rows < 1_000_000)
    return `~${(rows / 1000).toFixed(rows < 10_000 ? 1 : 0)}K`;
  if (rows < 1_000_000_000)
    return `~${(rows / 1_000_000).toFixed(rows < 10_000_000 ? 1 : 0)}M`;
  return `~${(rows / 1_000_000_000).toFixed(1)}B`;
};

export const TableColumnHoverCard: React.FC<Props> = ({ preview }) => {
  const data = preview.status === "success" ? preview.data : null;
  // Header name is always known by the caller, but we don't have it
  // until the first successful fetch — fall back to nothing in
  // loading/error so the layout doesn't reflow.
  const tableName = data?.name ?? "";
  const rowsLabel =
    data?.rowCount != null
      ? formatRows(data.rowCount, true)
      : data?.estimatedRowsFallback != null
        ? formatRows(data.estimatedRowsFallback, false)
        : null;
  const columnCount = data ? data.columns.length : null;

  return (
    <>
      <HoverCardHeader title={tableName} />

      <div className="flex flex-row flex-wrap gap-2 px-2.5 py-1.5 cursor-default">
        <ItemMetric
          metric_type={MetricType.ColumnsInTable}
          value={columnCount}
        />
        {rowsLabel != null && (
          <ItemMetric metric_type={MetricType.NActiveRows} value={rowsLabel} />
        )}
      </div>

      <ColumnsBody preview={preview} />
    </>
  );
};

const ColumnsBody: React.FC<{ preview: TablePreviewState }> = ({ preview }) => {
  if (preview.status === "idle" || preview.status === "loading") {
    return <HoverCardSkeleton />;
  }
  if (preview.status === "error") {
    return <HoverCardNote>Columns not visible to this user.</HoverCardNote>;
  }
  if (preview.data.columns.length === 0) {
    return <HoverCardNote>No columns visible.</HoverCardNote>;
  }

  return (
    <HoverCardList>
      {preview.data.columns.map((col) => (
        <HoverColumnRow key={col.key} col={col} />
      ))}
    </HoverCardList>
  );
};

const HoverColumnRow: React.FC<{ col: ColumnPreview }> = ({ col }) => (
  <HoverCardRow>
    <span className="text-fg-regular truncate">{col.name}</span>
    <span className="font-mono text-[10.5px] text-fg-muted-regular whitespace-nowrap">
      {col.typeLabel}
    </span>
  </HoverCardRow>
);
